/* zoe-screens.ts — Zoe's screen catalog for the OPTIONAL ScreenNavigator add-on. */

import type { ScreenDef } from './ScreenNavigator';
import { ZOE_SCREEN_ROUTES, type RawZoeScreen } from './zoe-screens.generated';
import { MARKED_UP_LETTER_SCREENS } from '../lib/markedUpLetterScreens';

const PROTO = 'https://zoe-diagnostics-prototype.netlify.app/proto.html';

const WIREFRAME_BY_ROUTE: Record<string, string> = {
  '/today': `${PROTO}#/s-10`,
  '/portfolio': `${PROTO}#/s-25`,
  '/deals': `${PROTO}#/s-26`,
  '/onboarding/oath': `${PROTO}#/s-05`,
};

// Sample values for params that CAN be synthesized (unlike:token).
const SAMPLE_CODENAME = 'project-lighthouse';
const SAMPLE_PORTCO_SLUG = 'acme-robotics';
const SAMPLE_SOURCE_ID = 'quickbooks';
const SAMPLE_LEGAL_PAGE = 'privacy';
const SAMPLE_MUL_SLUG = MARKED_UP_LETTER_SCREENS.some((s) => s.slug === 'today') ? 'today' : (MARKED_UP_LETTER_SCREENS[0]?.slug ?? 'today');

/** Params that cannot be synthesized — a real signed token is required. */
const UNSYNTHESIZABLE_PARAMS = new Set(['token']);

function extractCurrentCodename(pathname: string): string | null {
  const m = pathname.match(/^\/deal\/([^/]+)(?:\/|$)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** Fills in a route template's params. */
function resolveRoute(raw: RawZoeScreen, currentCodename: string | null): { route: string; disabledReason?: string } {
  if (raw.params.length === 0) return { route: raw.route };

  const unsynthesizable = raw.params.filter((p) => UNSYNTHESIZABLE_PARAMS.has(p));
  if (unsynthesizable.length > 0) {
    return {
      route: raw.route,
      disabledReason: `Requires a real ${unsynthesizable.join('/')} — signed tokens can't be generated for QA. This screen exists at ${raw.route}; reach it via a real invite/share/survey link instead.`,
    };
  }

  let route = raw.route;
  for (const param of raw.params) {
    let value: string;
    switch (param) {
      case 'codename':
        value = currentCodename ?? SAMPLE_CODENAME;
        break;
      case 'slug':
        // Both /marked-up-letter/:slug and /portco/:slug use this param name; disambiguate by route prefix.
        value = raw.route.startsWith('/portco/') ? SAMPLE_PORTCO_SLUG : SAMPLE_MUL_SLUG;
        break;
      case 'page':
        value = SAMPLE_LEGAL_PAGE;
        break;
      case 'id':
        value = SAMPLE_SOURCE_ID;
        break;
      default:
        // Unknown future param — fail visibly in the catalog rather than silently emitting a literal ":param" dead link.
        return {
          route: raw.route,
          disabledReason: `Unrecognized route param ":${param}" — zoe-screens.ts needs a sample value added for it.`,
        };
    }
    route = route.replace(`:${param}`, encodeURIComponent(value));
  }
  return { route };
}

/** Builds the live ScreenNavigator catalog for the CURRENT pathname. */
export function buildZoeScreens(currentPathname: string): ScreenDef[] {
  const currentCodename = extractCurrentCodename(currentPathname);
  return ZOE_SCREEN_ROUTES.map((raw) => {
    const { route, disabledReason } = resolveRoute(raw, currentCodename);
    const screen: ScreenDef = {
      id: raw.id,
      title: raw.title,
      section: raw.section,
      route,
      authRequired: raw.authRequired,
    };
    if (disabledReason) screen.disabledReason = disabledReason;
    const wireframe = WIREFRAME_BY_ROUTE[raw.route];
    if (wireframe) screen.wireframe = wireframe;
    return screen;
  });
}

export const ZOE_SCREENS: ScreenDef[] = buildZoeScreens('/');
